import {
  useWSSnackbar,
  WSButton,
  WSButtons,
  WSFlexBox,
  WSFlexGrid,
  WSForm,
  WSIcon,
  WSSelect,
  WSText,
  WSTextInput
} from "@wingspanhq/fe-component-library";
import React from "react";
import * as Yup from "yup";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { NEC1099FilingContainer } from "../components/NEC1099FilingContainer";

import styles from "./styles.module.scss";
import { SetupFlowRouteComponentProps } from "../../components/SetupFlow/SetupFlow";
import {
  useCreateBulkCalculate1099Batch,
  useUpdatePayrollSettings
} from "../../query/payments/mutations";
import {
  useCollaboratorsQuery,
  usePayrollSettings
} from "../../query/payments/queries";
import { useActivities, useMemberProfile } from "../../query/users/queries";
import { useUserId } from "../../query/hooks/helpers";
import { useUpdateActivity } from "../../query/users/mutations";
import { get1099SetupProgress } from "../../query/users/selectors";
import { WSQueries } from "../../query/WSQuery";
import { TAX_ID_REQUIRED_STATES } from "../utils";
import { WSErrorMessage } from "../../components/WSErrorMessage/WSErrorMessage";
import { ErrorContextKey } from "../../services/platform";

const StateTaxIdField: React.FC = () => {
  const { control } = useFormContext();
  const { fields: stateTaxIds, append, remove } = useFieldArray<IStateTaxId>({
    name: "stateTaxIds",
    control
  });

  const onAddState = () => {
    append({
      state: "",
      taxId: ""
    });
  };

  const onRemoveState = (index: number) => {
    remove(index);
  };

  return (
    <>
      {stateTaxIds.map((stateTaxId, index) => {
        return (
          <WSFlexGrid
            className={styles.stateTaxIdField}
            container
            key={stateTaxId.id}
          >
            <WSFlexBox.Center justify="space-between">
              <WSFlexGrid item mobile="5">
                <WSForm.Field
                  defaultValue={stateTaxId.state}
                  name={`stateTaxIds[${index}].state`}
                  component={WSSelect}
                  componentProps={{
                    options: TAX_ID_REQUIRED_STATES,
                    placeholder: "Select",
                    searchable: true
                  }}
                  label="State"
                />
              </WSFlexGrid>
              <WSFlexGrid item mobile="5">
                <WSForm.Field
                  defaultValue={stateTaxId.taxId}
                  label="Tax ID number"
                  name={`stateTaxIds[${index}].taxId`}
                  component={WSTextInput}
                  componentProps={{
                    formatOptions: {
                      numericOnly: true,
                      blocks: [2, 7],
                      delimiters: ["-"],
                      delimiterLazyShow: true
                    },
                    fsExclude: true
                  }}
                />
              </WSFlexGrid>
              <WSFlexGrid item mobile="1">
                <WSIcon
                  mt="M"
                  name="circleMinus"
                  onClick={() => onRemoveState(index)}
                  color="blue"
                />
              </WSFlexGrid>
            </WSFlexBox.Center>
          </WSFlexGrid>
        );
      })}
      <WSButton.Link
        type="button"
        icon="circlePlus"
        onClick={onAddState}
        name="addState"
      >
        Add state
      </WSButton.Link>
    </>
  );
};

interface StateFilingFormValues {
  stateTaxIds: IStateTaxId[];
}

export interface IStateTaxId {
  state: string;
  taxId: string;
}

export const StateFiling: React.FC<SetupFlowRouteComponentProps> = ({
  onBack,
  onNext
}) => {
  const history = useHistory();
  const userId = useUserId();
  const { openSnackbar } = useWSSnackbar();

  const qActivity = useActivities(userId);
  const qMemberProfile = useMemberProfile(userId);
  const qPayrollSettings = usePayrollSettings(userId);
  // prefetching collaborators for the next step
  const qCollaborators = useCollaboratorsQuery();

  const [updateActivity, updateActivityMeta] = useUpdateActivity(userId);
  const [
    updatePayrollSettings,
    updatePayrollSettingsMeta
  ] = useUpdatePayrollSettings(userId);
  const [
    createBulkCalculate1099Batch,
    createBulkCalculate1099BatchMeta
  ] = useCreateBulkCalculate1099Batch();

  const stateTaxIdMapToArray = (stateTaxIdMap: { [key: string]: string }) => {
    const _stateTaxIds: IStateTaxId[] = [];
    Object.keys(stateTaxIdMap).forEach(state => {
      _stateTaxIds.push({
        state,
        taxId: stateTaxIdMap[state]
      });
    });
    return _stateTaxIds;
  };

  const initBulkCalculate1099Batch = async () => {
    const response = await createBulkCalculate1099Batch({});
    if (response?.bulkCalculation1099BatchId) {
      onNext({
        bulkCalculation1099BatchId: response?.bulkCalculation1099BatchId
      });
    } else {
      openSnackbar({
        type: "warning",
        icon: {
          name: "warning",
          color: "red500"
        },
        message: "Could not initiate generate 1099-NEC forms."
      });
    }
  };

  const onFormSubmit = async (data: { stateTaxIds: IStateTaxId[] }) => {
    const stateTaxIdMap = (data.stateTaxIds || []).reduce((acc: any, item) => {
      acc[item.state] = item.taxId;
      return acc;
    }, {});
    await updatePayrollSettings({
      calculationSettings1099: {
        stateTaxId: stateTaxIdMap
      }
    });

    const nec1099SetupProgress = get1099SetupProgress(qActivity.data);
    await updateActivity({
      flows: {
        nec1099Setup: {
          ...nec1099SetupProgress,
          complete: true
        }
      }
    });
    // let's initiate 1099 calculation
    await initBulkCalculate1099Batch();
  };

  return (
    <NEC1099FilingContainer title="State filing">
      <WSQueries queries={{ qMemberProfile, qPayrollSettings }}>
        {({
          qMemberProfile: { data: member },
          qPayrollSettings: { data: payrollSettings }
        }) => {
          const { address } = member.profile;
          const userStateTaxIds = stateTaxIdMapToArray(
            payrollSettings.calculationSettings1099?.stateTaxId ?? {}
          );
          let defaultStateTaxIds = [{ state: "", taxId: "" }];
          if (userStateTaxIds.length > 0) {
            defaultStateTaxIds = userStateTaxIds;
          } else if (
            TAX_ID_REQUIRED_STATES.map(state => state.value).includes(
              address?.state ?? ""
            )
          ) {
            defaultStateTaxIds = [{ state: address?.state ?? "", taxId: "" }];
          }
          const defaultValues = {
            stateTaxIds: defaultStateTaxIds
          };
          return (
            <>
              <WSText.Heading2 mb="M">State filing settings</WSText.Heading2>
              <WSText.Paragraph color="grey700" mb="M">
                Some states require a state Tax ID number fo file 1099-NECs at
                the state level. If you would like to submit any 1099-NECs for
                state filing, add the applicable states and withholding or
                registration ID numbers below.
              </WSText.Paragraph>

              <WSForm<StateFilingFormValues>
                mb="M"
                validationSchema={Yup.object().shape({
                  stateTaxIds: Yup.array().of(
                    Yup.object().shape({
                      state: Yup.string().required("Required"),
                      taxId: Yup.string().required("Required")
                    })
                  )
                })}
                onSubmit={onFormSubmit}
                defaultValues={defaultValues}
              >
                <StateTaxIdField />

                <WSErrorMessage
                  mt="M"
                  contextKey={ErrorContextKey.PayrollSettings}
                  error={updatePayrollSettingsMeta.error}
                />
                <WSButtons mt="XL" mb="M">
                  <WSButton.Secondary
                    className={styles.actionBtn}
                    onClick={onBack}
                  >
                    Back
                  </WSButton.Secondary>
                  <WSForm.SubmitButton
                    className={styles.actionBtn}
                    data-testid="confirmPayerInfo"
                    loading={
                      updatePayrollSettingsMeta.isLoading ||
                      updateActivityMeta.isLoading ||
                      createBulkCalculate1099BatchMeta.isLoading
                    }
                  >
                    Continue
                  </WSForm.SubmitButton>
                </WSButtons>

                <WSText.LabelSmall color="grey600" mb="M">
                  This is the last step before we generate your 1099-NEC forms.
                  If you skip this step, you will not be able to revisit it
                  without regenerating all 1099-NEC forms. To start over and
                  generate new forms, contact support@wingspan.app.
                </WSText.LabelSmall>

                <WSFlexBox.Center>
                  <WSButton.Link onClick={initBulkCalculate1099Batch}>
                    Skip
                  </WSButton.Link>
                </WSFlexBox.Center>
              </WSForm>
            </>
          );
        }}
      </WSQueries>
    </NEC1099FilingContainer>
  );
};
